/**
 * Projected delivery, opens and clicks — the figures a workspace sees while no
 * provider has reported anything at all.
 *
 * ## WHY THIS EXISTS, AND WHY IT IS NOT THE THING THE PROJECT REFUSED
 *
 * Slice 22 refused "a flag that fabricates figures inside `apps/` or
 * `packages/`", and it was right to. Two critical defects have shipped here and
 * both were a confident number drawn over data that did not exist. Nothing in
 * this file repeals that.
 *
 * What the owner actually asked for, three times, is different from what was
 * refused, and the difference is the whole design:
 *
 *   - **It is not a flag.** There is no environment variable, no build switch
 *     and nothing that can be left turned on. {@link mayProject} reads
 *     `lastReceivedAt` — the newest stored `delivery_events` row for that
 *     provider, of any type — so the first real notification of any kind
 *     extinguishes it, in the same transaction the figures were read from.
 *   - **It substitutes for nothing.** The base is the *unreported remainder*,
 *     never the accepted total. `delivered: 0` stays on screen, measured and
 *     unmodified, and the projection sits underneath it as a second statement.
 *     As reports arrive the base shrinks on its own.
 *   - **It is unreachable from every write path.** It is computed after the
 *     read transaction closes, it never becomes a row, and the shape below is
 *     not assignable to any measured shape in the product (see the next
 *     section).
 *
 * What is *not* different, and must be said in the commit and on the screen: it
 * is a number nobody measured. The chip and {@link PROJECTION_ASSUMPTION_COPY}
 * are not decoration.
 *
 * ## THE DANGEROUS ASSUMPTION IS 100% DELIVERY, NOT 30% OR 20%
 *
 * Opens and clicks are harmless guesses; nothing downstream turns on them.
 * "Everything arrived" is a claim about deliverability, made on an account that
 * has never had a report back — and if the list is in fact bouncing, it says the
 * opposite of the truth in the direction that stops somebody looking.
 *
 * The mitigation is real but partial, and it is the reason the base is
 * `awaitingEvents`: one real bounce both shrinks the base *and* flips
 * `lastReceivedAt` non-null, so the projection does not survive the first
 * genuine piece of evidence against it.
 *
 * ## WHAT HOLDS IT AWAY FROM THE THINGS THAT MATTER
 *
 * Five barriers, each a compile or parse error rather than a review finding:
 *
 *   1. {@link ProjectedFigures} has `basis` and `unreported` and lacks
 *      `queued`/`sent`/`bounced`/`failed`/`unknown`, `settled`, `reading`,
 *      `threshold` and `band`. So it is not assignable to `MessageStatusCounts`,
 *      `OverviewSendingHealth`, `EngagementRate`, `WaReadRate` or
 *      `OverviewRateKpi`, and none of them is assignable here. This is the same
 *      technique `engagementRateSchema` uses to keep an open rate out of a KPI
 *      tile, and the reason it is worth repeating is that it cannot be reworded
 *      away by a later reader who finds the comment unconvincing.
 *   2. It lives on exactly two response schemas — `overviewResponseSchema` and
 *      `campaignResultsResponseSchema` — both `.strict()`.
 *      `sendRiskMeasurementSchema` — the halt gate — has no such member, and
 *      `readEmailRisk` issues its own aggregate over `delivery_events` on a
 *      transaction handed to it. The halt cannot receive one by dependency
 *      direction, not by discipline.
 *   3. It is built after `tenantDb.run()` closes at both call sites, so it is
 *      not in lexical scope for any write. `CreditsService.consume` takes
 *      `{ channel, messageId }` and has no numeric parameter one could travel
 *      through even deliberately.
 *   4. It never becomes a row. No migration, no column, no `delivery_events`
 *      write, no `credit_ledger` write — so a future report generator has
 *      nothing to pick up by accident.
 *   5. `unreported` is `.min(1)`. A fully reported workspace cannot carry a
 *      projection even if {@link mayProject} were miswired, because the shape
 *      does not parse.
 *
 * ## WHERE IT DELIBERATELY DOES NOT GO
 *
 * Not on the activity charts: a projected per-day delivered would make a
 * stacked bar exceed that day's own accepted, and `overview-activity`'s bucket
 * contract forbids a rate on a bucket for exactly that reason. Not on Analytics,
 * which computes its own delivered from `messages.status` and will visibly
 * disagree — that disagreement is stated there in one line rather than resolved
 * by spreading this to a third assembly point. Not on suppressions, in either
 * direction: there is no projected *bounce* here and there must never be one,
 * because a suppression written on an assumption removes a real address.
 */
import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/* The assumptions, as data                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The rates, in one place, as the numbers the owner gave.
 *
 * They are `const` rather than configuration on purpose. A tenant-settable
 * projection rate is a number a workspace could tune until the dashboard looked
 * good, which is a worse product than no projection at all.
 */
export const PROJECTED_DELIVERY_RATE = 1.0;
export const PROJECTED_OPEN_RATE = 0.3;
export const PROJECTED_CLICK_RATE = 0.2;

/**
 * Always `'assumed_rates'`.
 *
 * A `z.literal` rather than an enum, for the same reason `ENGAGEMENT_CONFIDENCE`
 * is one: an enum with a second member is an invitation for somebody to add
 * `'measured'` to it one day, and at that moment every guarantee in this file
 * becomes a comment.
 */
export const PROJECTION_BASIS = 'assumed_rates' as const;

export const PROJECTION_ASSUMPTION_VALUES = ['all_delivered', 'open_rate', 'click_rate'] as const;

export const projectionAssumptionSchema = z.enum(PROJECTION_ASSUMPTION_VALUES);
export type ProjectionAssumption = z.infer<typeof projectionAssumptionSchema>;

/**
 * What each assumption says, in the reader's words.
 *
 * Total over {@link PROJECTION_ASSUMPTION_VALUES}, so a new assumption is a
 * compile error here rather than a blank line under a figure. These render as a
 * visible list in the row — never a tooltip, which `verify-page-hardening` bans
 * on a screen file, and never folded, because a projection whose assumptions are
 * one click away is an exact figure as far as a skimmer is concerned.
 *
 * `all_delivered` is deliberately the bluntest of the three. It is the one that
 * could mislead.
 */
export const PROJECTION_ASSUMPTION_COPY: Readonly<Record<ProjectionAssumption, string>> = {
  /*
   * THE BLUNTEST OF THE THREE, ON PURPOSE.
   *
   * The first draft ended "so none of it is confirmed", and review was right
   * that this reads as paperwork pending rather than as a number that may be
   * wrong. It is the one assumption with a direction: if the list is stale, it
   * is wrong the way that stops somebody looking. The sentence says so.
   */
  all_delivered:
    'Assumes all of them arrived. Wend has no evidence either way — and if the list is stale, this is wrong in the direction that looks healthy.',
  /*
   * Built from the constants rather than typed beside them. `VDM_COST_DISCLOSURE`
   * is the precedent in this package, and the reason is the one the clicks guard
   * below already gives: a later edit to a rate should not be able to leave a
   * sentence saying the old number. There is nothing to keep in step by hand.
   */
  open_rate: `Assumes ${String(Math.round(PROJECTED_OPEN_RATE * 100))} of every 100 are opened.`,
  click_rate: `Assumes ${String(Math.round(PROJECTED_CLICK_RATE * 100))} of every 100 have a link clicked.`,
};

/* -------------------------------------------------------------------------- */
/* The shape                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Projected figures over the messages nothing has been reported for.
 *
 * `unreported` is carried rather than left implicit because it is the honest
 * denominator: a reader who sees "1,116 projected as delivered" is entitled to
 * know it is a projection over 1,116 unreported messages and not a share of
 * something larger.
 */
export const projectedFiguresSchema = z
  .object({
    /** Always {@link PROJECTION_BASIS}. See its doc for why it is a literal. */
    basis: z.literal(PROJECTION_BASIS),
    /**
     * The messages this is projected over: accepted, with nothing reported back.
     *
     * `.min(1)` is barrier 5. A workspace whose reports have all arrived has
     * nothing to project over, and this makes that unrepresentable rather than
     * merely unlikely.
     */
    unreported: z.number().int().min(1),
    delivered: z.number().int().min(0),
    opened: z.number().int().min(0),
    clicked: z.number().int().min(0),
    /**
     * Never empty. A projection displayed without its assumptions is a
     * measurement as far as the reader is concerned — the same rule, and the
     * same `.min(1)`, that `engagementRateSchema.caveats` carries.
     */
    assumptions: z.array(projectionAssumptionSchema).min(1),
  })
  .strict();
export type ProjectedFigures = z.infer<typeof projectedFiguresSchema>;

/* -------------------------------------------------------------------------- */
/* The gate                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Whether anything may be projected for this provider at all.
 *
 * `lastReceivedAt` is the newest `delivery_events.created_at` for the provider,
 * of **any** event type — a bounce, a complaint, an open, anything. `null` means
 * no report of any kind has ever reached Wend.
 *
 * This is the off-switch, and it is the reason this design needs no flag: the
 * first genuine notification flips it non-null in the same transaction the
 * figures are read from, so the projection is absent from the very next
 * response. Nobody has to remember to turn it off, and nothing can be shipped
 * with it left on.
 *
 * It is deliberately **not** gated on `setup === 'ready'`. A workspace whose
 * reports are configured but silent still has nothing, and `lastReceivedAt`
 * already says so — gating on setup would mean a correctly-configured workspace
 * waiting for its first send saw nothing where an unconfigured one saw a
 * projection, which is backwards.
 *
 * ## A `send` ACKNOWLEDGEMENT COUNTS, AND THAT IS THE POINT
 *
 * SES emits `send` when it accepts a message through a configuration set, and
 * Wend stores it like any other event. So for a workspace whose reports are
 * wired, `lastReceivedAt` goes non-null at the moment of the **first send** —
 * before one delivery, bounce, open or click has been reported.
 *
 * This reads at first like the gate firing too early. It is the opposite, and
 * the distinction is worth stating because the obvious "fix" — gating on outcome
 * events only — is wrong:
 *
 *   - A workspace whose `send` events are arriving has a working reports
 *     pipeline. Its real deliveries and bounces follow **within minutes**. An
 *     assumption there would be replaced almost immediately, and for those few
 *     minutes it would sit on a screen claiming things about mail whose actual
 *     outcome was already in flight.
 *   - A workspace with no events at all has no pipeline. Nothing is coming, not
 *     in minutes and not ever, until somebody configures it. That is the only
 *     workspace an assumption helps, and it is the one this answers.
 *
 * So the question this gate asks is *"does anything at all report back here?"*
 * rather than *"has an outcome arrived yet?"*, and the narrow window between
 * them belongs to the side that waits. Anything else means guessing next to
 * evidence that is already on its way.
 */
export function mayProject(reports: { readonly lastReceivedAt: string | null } | null): boolean {
  return reports !== null && reports.lastReceivedAt === null;
}

/* -------------------------------------------------------------------------- */
/* The builder                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The only supported way to build one, and the only place the arithmetic lives.
 *
 * Returns `null` rather than a zeroed shape when there is nothing to project —
 * no reports have arrived but nothing is waiting either — so a caller cannot
 * render an empty projection by forgetting to check.
 *
 * **There was an `includeEngagement: false` branch here and it is gone.** It
 * returned `opened: 0, clicked: 0` with only the delivery assumption attached —
 * and {@link ProjectedFigures} has no way to say "this zero is not a
 * measurement", so a row built from it would have printed *Opened 0, Clicked 0*
 * with nothing qualifying either. That is the precise defect this file exists to
 * prevent, sitting unused in the constructor waiting for the first caller who
 * wanted WhatsApp. It was reachable from nowhere, which made it worse rather
 * than better: nothing would have failed. The parameter comes back, if it comes
 * back, with a shape that cannot express an uncaveated figure.
 *
 * `clicked` is capped at `opened` as well as computed from its own rate. With
 * 30% and 20% of the same base the rates already guarantee it, but the guarantee
 * belongs in the code rather than in the reader's arithmetic: "more clicks than
 * opens" is the first thing that would make a reader stop trusting the row, and
 * a later edit to either constant should not be able to produce it.
 */
export function projectUnreported(input: { readonly unreported: number }): ProjectedFigures | null {
  if (!Number.isFinite(input.unreported) || input.unreported < 1) return null;

  const unreported = Math.floor(input.unreported);
  const delivered = Math.round(unreported * PROJECTED_DELIVERY_RATE);
  const opened = Math.round(delivered * PROJECTED_OPEN_RATE);
  const clicked = Math.min(opened, Math.round(delivered * PROJECTED_CLICK_RATE));

  return {
    basis: PROJECTION_BASIS,
    unreported,
    delivered,
    opened,
    clicked,
    assumptions: ['all_delivered', 'open_rate', 'click_rate'],
  };
}

/**
 * The Overview's projection, from figures the response already holds.
 *
 * Zero new queries: `sending` and `reports` are both in hand when
 * `OverviewService.read()` assembles its response, which is what makes this the
 * narrowest place in the product a derived figure could be introduced — the
 * numbers to derive from and the evidence to gate on are available at the same
 * instant, and nowhere else is that true.
 *
 * The base is `awaitingEvents`, never `accepted`. That is the most important
 * arithmetic decision in the design and the header explains it; the short form
 * is that projecting over the total would overwrite what is measured, and
 * projecting over the remainder is strictly additive to it.
 *
 * Email only. WhatsApp has no click at all, and Meta's read receipts are a
 * different measurement with a different contract.
 */
/**
 * A single campaign's projection, from figures its results response already
 * holds.
 *
 * The campaign twin of {@link projectFromOverview}, and deliberately the same
 * shape rather than a variation on it: the base is the unreported remainder,
 * the gate asks that nothing accepted has any terminal outcome, and both are
 * expressed in this file so the two screens cannot drift into disagreeing about
 * what a projection means.
 *
 * `campaignResultsReading` already computes `accepted = sent + delivered +
 * bounced` and `awaitingEvents = sent` as a pure function, so the equality below
 * is the same question the Overview asks, written in the vocabulary this
 * response uses. `failed` is asked separately because a rejected message is
 * never in `accepted` at all — it shrinks nothing, so the equality cannot see
 * it, and it is exactly the evidence the Overview's gate was widened to catch.
 *
 * **Email only.** WhatsApp has no click to project, its read receipts are a
 * different measurement under a different contract with its own `floor`
 * confidence, and assuming a delivery rate for a channel whose own contract
 * refuses to state one would be inventing agreement between two providers.
 *
 * **The two bases do not sum, and a reader will notice.** The Overview projects
 * over a 30-day window; this projects over a campaign's whole life. A campaign
 * older than that window contributes here and not there, so adding these up will
 * not give the Overview's total. That is the same class of honest disagreement
 * `overview-sending.ts` already records between the halt gate and the tiles, and
 * it is stated rather than resolved: making them agree would mean windowing the
 * campaign, which answers a question nobody asked of a campaign page.
 */
export function projectFromCampaign(
  results: {
    readonly messages: {
      readonly sent: number;
      readonly delivered: number;
      readonly bounced: number;
      readonly failed: number;
    };
    readonly complaints: number;
  },
  reports: { readonly lastReceivedAt: string | null } | null,
  channel: 'email' | 'whatsapp',
): ProjectedFigures | null {
  if (channel !== 'email') return null;
  if (!mayProject(reports)) return null;

  const m = results.messages;
  const accepted = m.sent + m.delivered + m.bounced;
  if (m.sent !== accepted) return null;
  if (m.failed > 0 || results.complaints > 0) return null;

  return projectUnreported({ unreported: m.sent });
}

export function projectFromOverview(
  sending: {
    readonly awaitingEvents: number;
    readonly accepted: number;
    readonly complaints: number;
  },
  reports: { readonly lastReceivedAt: string | null } | null,
): ProjectedFigures | null {
  if (!mayProject(reports)) return null;

  /*
   * THE SECOND GATE, AND IT IS NOT REDUNDANT.
   *
   * It asks one question @ `awaitingEvents === accepted` @ and that single
   * equality is deliberately chosen over the three outcome counts it replaced.
   * See below for what those could not see.
   *
   * `mayProject` asks the `delivery_events` table; `settled` is counted from
   * `messages.status`. In production the two move together, because the webhook
   * that sets a message to `delivered` or `bounced` writes the event row in the
   * same transaction — so on paper this line can never fire.
   *
   * "On paper it cannot happen" is how the two criticals shipped. The two
   * numbers have different writers, different tables and different failure
   * modes: an ingest that updated statuses and failed before its event insert,
   * a backfill, a restore from a partial dump, or a fixture built by hand would
   * each produce terminal outcomes with no event rows behind them — and every
   * one of those is a workspace holding real evidence about its own
   * deliverability while this function offered to assume all of it arrived.
   *
   * So either piece of evidence is enough to refuse. A projection is for a
   * workspace that knows *nothing*, not one whose knowledge is merely recorded
   * somewhere this gate did not look.
   *
   * **Not `settled`.** `settled` is `delivered + bounced` *within the rate
   * cohort*, and slice 24 defined that cohort as mail through Wend's own
   * reporting configuration set. Mail Wend only hears about through the sending
   * identity's own SNS topic is excluded from it on purpose. So a workspace can
   * hold **25 known bounces with `settled === 0`**, and a `settled` gate would
   * have offered it an assumption that everything arrived over evidence that
   * twenty-five things did not. Found by a fixture, not by reading.
   *
   * **And not the three outcome counts either, which was the second draft.**
   * `delivered > 0 || bounced > 0 || complaints > 0` still could not see a
   * *rejection*: `awaitingEvents` is `accepted - (delivered + bounced + failed)`,
   * so a `failed` message shrinks the base while no count reports it — and
   * `OverviewSendingHealth` has no `failed` member, so this function's parameter
   * type could not have reached one even deliberately. A restore landing 300
   * rejections and 200 unreported sends would have passed both gates and claimed
   * all 200 arrived.
   *
   * `awaitingEvents === accepted` asks the same question and cannot be blind to a
   * status it has no field for: it is true exactly when nothing accepted in this
   * window has any terminal outcome recorded, whatever that outcome was.
   * `complaints` is asked separately because a complaint does not change
   * `awaitingEvents` — a message can be delivered, complained about, and still
   * counted as settled rather than awaiting.
   *
   * **What this still does not close, stated rather than papered over.** These
   * counts are windowed to the last 30 days and `lastReceivedAt` is all-time. A
   * workspace whose bounces are 45 days old, whose unreported sends are 3 days
   * old, and which has no `delivery_events` rows at all — which takes a partial
   * restore or a hand-built fixture, since the ingest writes both together —
   * would still pass. The screen refuses it anyway, because
   * `noOutcomeReported()` gates the branch that renders this and is false as
   * soon as anything is settled. That is a real barrier and it is named here
   * rather than counted among the five, because it lives in a different package
   * and a second consumer of `projection` would not inherit it.
   */
  if (sending.awaitingEvents !== sending.accepted || sending.complaints > 0) return null;

  return projectUnreported({ unreported: sending.awaitingEvents });
}
